'use client';

import { type ComponentProps, createContext, type ReactNode, useContext, useId } from 'react';
import { cn } from './cn';

type FieldIds = { id: string; describedBy?: string; invalid: boolean };
const FieldContext = createContext<FieldIds | null>(null);

/** The control inside a Field: its id, the id of its help or error, and whether it is invalid. */
export function useFieldControl(): FieldIds | null {
  return useContext(FieldContext);
}

/**
 * A label above its control, an optional help line under it, and the error in place of the help.
 * Mark what is optional rather than what is required.
 */
export function Field({
  label,
  optional,
  hint,
  error,
  children,
  className,
  id: idProp,
}: {
  label: ReactNode;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Id of the control, when something outside the field points at it. */
  id?: string;
}) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const noteId = `${id}-note`;
  const note = error || hint;
  return (
    <FieldContext.Provider
      value={{ id, describedBy: note ? noteId : undefined, invalid: Boolean(error) }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        <label htmlFor={id} className="text-crow-compact font-semibold text-crow-text">
          {label}
          {optional ? (
            <span className="ml-1.5 text-crow-compact font-normal text-crow-text-3">
              facultatif
            </span>
          ) : null}
        </label>
        {children}
        {note ? (
          <p
            id={noteId}
            className={cn('text-crow-label', error ? 'text-crow-danger' : 'text-crow-text-3')}
          >
            {note}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/**
 * Where a text control sits. `raised`, the default: a field in the surface colour on the page.
 * `recessed`: sunk into whatever it sits on (the `field` token), for a dense tool where a raised
 * field would read as a button (Crowdex). `bare`: no frame, fill or focus halo, for a control
 * inside a frame that shows its focus (`controlGroupClass`) or a title edited in place.
 */
export type ControlSurface = 'raised' | 'recessed' | 'bare';

const controlText =
  'w-full text-crow-body text-crow-text outline-none transition-[border-color,box-shadow,background-color] duration-[var(--crow-dur-1)] placeholder:text-crow-text-3';
const controlFrame = 'rounded-crow-control border border-crow-input';
// Literal class names throughout: Tailwind finds them by reading this file.
const controlFocus =
  'focus:border-crow-accent focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--crow-accent)_22%,transparent)] aria-invalid:border-crow-danger aria-invalid:shadow-[0_0_0_3px_color-mix(in_oklch,var(--crow-danger)_18%,transparent)]';
const surfaceFill = {
  raised: 'bg-crow-surface disabled:bg-crow-raised disabled:text-crow-text-3',
  recessed: 'bg-crow-field disabled:cursor-not-allowed disabled:opacity-50',
  bare: 'bg-transparent disabled:cursor-not-allowed disabled:opacity-50',
} as const satisfies Record<ControlSurface, string>;

/**
 * A text control's look without its height: frame, fill, text, placeholder, the accent border
 * and halo on focus, the danger ones when invalid. A project that sizes its controls itself
 * (Crowdex, on its density-aware heights) adds its own.
 */
export function controlLookClass(surface: ControlSurface = 'raised', className?: string): string {
  return cn(
    controlText,
    surface === 'bare' ? 'rounded-none border-0' : cn(controlFrame, 'px-3', controlFocus),
    surfaceFill[surface],
    className,
  );
}

/** The raised text control's look (`controlLookClass('raised')`). */
export const controlClass = controlLookClass('raised');

/** The heights of the charter's text controls. */
export const controlSizeClass = { sm: 'h-7 px-2.5 text-crow-compact', md: 'h-[34px]' } as const;
export type ControlSize = keyof typeof controlSizeClass;

/**
 * The frame of a group of controls (a field with an icon, a button or a hint beside its text): a
 * control's frame and fill, whose focus and invalid states follow the `bare` input, textarea or
 * select inside it rather than its own.
 */
export function controlGroupClass(
  surface: Exclude<ControlSurface, 'bare'> = 'raised',
  className?: string,
) {
  return cn(
    controlText,
    controlFrame,
    surfaceFill[surface],
    'has-[input:focus]:border-crow-accent has-[input:focus]:shadow-[0_0_0_3px_color-mix(in_oklch,var(--crow-accent)_22%,transparent)] has-[textarea:focus]:border-crow-accent has-[textarea:focus]:shadow-[0_0_0_3px_color-mix(in_oklch,var(--crow-accent)_22%,transparent)] has-[select:focus]:border-crow-accent has-[select:focus]:shadow-[0_0_0_3px_color-mix(in_oklch,var(--crow-accent)_22%,transparent)]',
    'has-[[aria-invalid=true]]:border-crow-danger',
    className,
  );
}

export function Input({
  className,
  id,
  surface = 'raised',
  ...props
}: ComponentProps<'input'> & { surface?: ControlSurface }) {
  const field = useFieldControl();
  return (
    <input
      id={id ?? field?.id}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid || undefined}
      className={controlLookClass(surface, cn(controlSizeClass.md, className))}
      {...props}
    />
  );
}

export function Textarea({
  className,
  id,
  rows = 3,
  surface = 'raised',
  ...props
}: ComponentProps<'textarea'> & { surface?: ControlSurface }) {
  const field = useFieldControl();
  return (
    <textarea
      id={id ?? field?.id}
      rows={rows}
      aria-describedby={field?.describedBy}
      aria-invalid={field?.invalid || undefined}
      className={controlLookClass(surface, cn('min-h-16 py-2 leading-normal', className))}
      {...props}
    />
  );
}
